import { z } from 'zod/v4-mini';

import {
  ApiDataSourceSpec,
  apiDataSource,
} from '@/io/datasource/ApiDatasource';
import {
  CarePlanDirectives,
  carePlanDirectivesSchema,
} from '@/models/CarePlanDirectivesModel';
import { CarePlans, carePlansSchema } from '@/models/CarePlanModel';
import { CareTeam, careTeamSchema } from '@/models/CareTeamModel';
import { Contact, contactSchema } from '@/models/ContactModel';
import { CreatePatientModel } from '@/models/CreatePatientModel';
import {
  DiabetesDataDisplayParams,
  diabetesDisplayModeSchema,
} from '@/models/DiabetesDisplayModeModel';
import { EditTherapeuticObjectivesData } from '@/models/EditTherapeuticObjectivesModel';
import {
  HomeServiceProvider,
  homeServiceProviderSchema,
} from '@/models/HomeServiceProviderModel.ts';
import {
  NurseReinclusionNudge,
  nurseReinclusionNudgeSchema,
} from '@/models/NurseReinclusionRequestModel';
import { PatientEvent, patientEventsSchema } from '@/models/PatientEventModel';
import {
  Patient,
  PatientID,
  patientIDSchema,
  patientSchema,
} from '@/models/PatientModel';
import {
  PatientList,
  patientListItemSchema,
} from '@/models/PatientSummaryModel.ts';
import { RenewCarePlanData } from '@/models/RenewCarePlanModel';
import { RenewalNudge, renewalNudgeSchema } from '@/models/RenewalNudgeModel';
import { ValidateOrRejectIdentity } from '@/models/identity/FrenchIdentity';
import {
  IdentityValidation,
  identityValidationSchema,
} from '@/models/identity/IdentityValidationModel';
import { ContactData } from '@/pages/patient-medical-file/contact-patient/ContactPatient.schema';
import { CarePlanCreationInfo } from '@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreation.ts';
import { PatientCreationInfo } from '@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientCreationInfo.ts';
import { cz } from '@/utils/zodUtils.ts';

export type PatientCreationSavedData = {
  patientInfo: Partial<PatientCreationInfo>;
  carePlan: Partial<CarePlanCreationInfo>;
};
/**
 * For health pro only
 */
export class ManagePatientRepository {
  constructor(private readonly datasource: ApiDataSourceSpec = apiDataSource) {}

  getPatientList = (): Promise<PatientList> => {
    return this.datasource.get(
      `practitioner/patients/list/all/`,
      cz.forcedArray(patientListItemSchema),
    );
  };

  getPatient = (id: string): Promise<Patient> => {
    return this.datasource.get(`practitioners/patients/${id}/`, patientSchema);
  };

  createPatient = (data: CreatePatientModel): Promise<PatientID> => {
    return this.datasource.post(
      `practitioners/patients/create/`,
      patientIDSchema,
      data,
    );
  };

  renewCarePlan = (
    patientId: string,
    data: RenewCarePlanData,
  ): Promise<PatientID> => {
    return this.datasource.post(
      `practitioners/renew-care-plan/${patientId}/`,
      patientIDSchema,
      data,
    );
  };

  editTherapeuticObjectives = (
    carePlanId: string,
    data: EditTherapeuticObjectivesData,
  ): Promise<PatientID> => {
    return this.datasource.patch(
      `practitioners/edit-care-plan-description/${carePlanId}/`,
      patientIDSchema,
      data,
    );
  };

  getCareTeam = (patientId: string): Promise<CareTeam> => {
    return this.datasource.get(
      `practitioners/patients/${patientId}/care-team/`,
      careTeamSchema,
    );
  };

  getCarePlans = (patientId: string): Promise<CarePlans> => {
    return this.datasource.get(
      `practitioners/patients/${patientId}/care-plans/`,
      carePlansSchema,
    );
  };

  getCarePlanDirectives = (patientId?: string): Promise<CarePlanDirectives> => {
    return this.datasource.get(
      patientId
        ? `practitioners/care-plan-directives/${patientId}/`
        : 'practitioners/care-plan-directives/',
      carePlanDirectivesSchema,
    );
  };

  getListContacts = (patientId: string): Promise<Contact[]> => {
    return this.datasource.get(
      `practitioners/patients/${patientId}/contacts/`,
      cz.forcedArray(contactSchema),
    );
  };

  getIdentitiesToValidate = (): Promise<IdentityValidation[]> => {
    return this.datasource.get(
      `practitioners/identities-to-validate/`,
      z.array(identityValidationSchema),
    );
  };

  getDiabetesDisplayMode = (
    patientId: string,
  ): Promise<DiabetesDataDisplayParams> => {
    return this.datasource.get(
      `practitioner/patients/${patientId}/diabetes-display-mode/`,
      diabetesDisplayModeSchema,
    );
  };

  validateOrRejectIdentity = (
    patientId: string,
    action: ValidateOrRejectIdentity,
  ): Promise<PatientID> => {
    return this.datasource.post(
      `practitioners/patients/${patientId}/validate-identity/${action}/`,
      patientIDSchema,
    );
  };

  createPatientContacts = (
    patientId: string,
    data: ContactData,
  ): Promise<Contact> => {
    return this.datasource.post(`/contacts/${patientId}/`, contactSchema, {
      first_name: data.firstname,
      last_name: data.lastname,
      email: data.email,
      phones: [{ number: data.phone, description: 'mobile' }],
      comment: data.comment,
      is_emergency_contact: data.isEmergencyContact,
      relationship_with_patient: data.relationshipWithPatient,
    });
  };

  updatePatientContacts = (
    patientId: string,
    contactId: string,
    data: Partial<ContactData>,
  ): Promise<Contact> => {
    return this.datasource.patch(
      `/contacts/${patientId}/${contactId}/`,
      contactSchema,
      {
        first_name: data.firstname,
        last_name: data.lastname,
        email: data.email,
        phones: [{ number: data.phone, description: 'mobile' }],
        comment: data.comment,
        is_emergency_contact: data.isEmergencyContact,
        relationship_with_patient: data.relationshipWithPatient,
      },
    );
  };

  deletePatientContacts = (
    patientId: string,
    contactId: string,
  ): Promise<undefined> => {
    return this.datasource.delete(
      `/contacts/${patientId}/${contactId}/`,
      cz.ignore(),
    );
  };

  deleteNurseReinclusionSuggestion = (
    patientId: string,
  ): Promise<undefined> => {
    return this.datasource.delete(
      `practitioner/reinclusions/${patientId}/`,
      cz.ignore(),
    );
  };

  getNurseReinclusionSuggestion = (
    patientId: string,
  ): Promise<NurseReinclusionNudge> => {
    return this.datasource.get(
      `practitioner/reinclusions/${patientId}/`,
      nurseReinclusionNudgeSchema,
    );
  };

  createNurseReinclusionSuggestion = (
    patientId: string,
  ): Promise<undefined> => {
    return this.datasource.post(
      `practitioner/reinclusions/${patientId}/`,
      cz.ignore(),
    );
  };

  getRenewalNudge = (patientId: string): Promise<RenewalNudge> => {
    return this.datasource.get(
      `practitioner/renewal-nudge/${patientId}/`,
      renewalNudgeSchema,
    );
  };

  refuseRenewal = (patientId: string): Promise<undefined> => {
    return this.datasource.post(
      `practitioner/renewal-nudge/${patientId}/`,
      cz.ignore(),
    );
  };

  listHSP = (): Promise<HomeServiceProvider[]> => {
    return this.datasource.get(
      '/practitioner/home-service-providers/list/all/',
      cz.forcedArray(homeServiceProviderSchema),
    );
  };

  getPatientEvents = (patientId: string): Promise<PatientEvent[]> => {
    return this.datasource.get(
      `practitioner/events/${patientId}/`,
      z.array(patientEventsSchema),
    );
  };
}
